import { AxisTickText } from '@/components/public/Visualisations/XYBubbles/Entities/Axis/AxisTickText'
import { XAxisLabelText } from '@/components/public/Visualisations/XYBubbles/Entities/Axis/XAxisLabelText'
import { YAxisLabelText } from '@/components/public/Visualisations/XYBubbles/Entities/Axis/YAxisLabelText'
import { XYBubbleMetadata } from '@/components/public/Visualisations/XYBubbles/Metadata/XYBubbleMetadata'
import {
  AxisTick,
  XAxisProps,
  YAxisProps,
} from '@/components/public/Visualisations/XYBubbles/types'
import { measureText } from '@/helpers/canvasHelpers'
import typedStore from '@/store/typedStore'
import { Coordinate2D, Rectangle, XYBubblesConfig, XYDimensionsMapping } from '@/types'
import { format } from 'd3-format'
import _ from 'lodash'

const BUBBLES_BOX_MARGIN = 100
const Y_TICKS_HORIZONTAL_MARGIN = 40
const MAX_Y_TICKS = 10

export interface XYBubblesLayout {
  xAxis: XAxisProps
  yAxis: YAxisProps
  visualisationBoundingRect: Rectangle
  axesBoundingRect: Rectangle
  bubblesBoundingRect: Rectangle
}

class XYBubblesLayoutCalculator {
  private config!: XYBubblesConfig
  private canvasBoundingRect!: Rectangle
  private maximumHeight!: number
  private visualisationBoundingRect!: Rectangle
  private dimensionsMappings: XYDimensionsMapping[] = []
  private bubbles: XYBubbleMetadata[] = []
  private axesBottomMargin: number = 0
  private axesBoundingRect!: Rectangle
  private bubblesBoundingRect!: Rectangle
  private xAxisTicks: Map<string, AxisTick> = new Map()
  private yAxisTicks: Record<number, AxisTick> = {}
  private yAxisValues: number[] = []
  private customLabelsByYValue: Record<number, string> = {}
  private yLowerBound: number = 0
  private yUpperBound: number = 1
  private yAxisZeroPosition?: Coordinate2D

  public calculate(
    config: XYBubblesConfig,
    dimensionsMappings: XYDimensionsMapping[],
    bubbles: XYBubbleMetadata[],
    canvasBoundingRect: Rectangle,
    maximumHeight: number,
  ): XYBubblesLayout {
    this.reset()

    this.config = config
    this.dimensionsMappings = dimensionsMappings
    this.bubbles = bubbles
    this.canvasBoundingRect = canvasBoundingRect
    this.maximumHeight = maximumHeight
    this.calculateVisualisationBoundingRect()

    this.populateXAxisTicks()
    this.calculateAxesBottomMargin()
    this.calculateBubblesScale()
    this.calculateAxesBoundingBoxHeight()
    this.calculateBubblesBoundingBoxHeight()
    this.populateYAxisTicks()
    this.calculateAxesBoundingBoxWidth()
    this.calculateBubblesBoundingBoxWidth()

    this.calculateXAxisTickPositions()
    this.calculateYAxisTickPositions()

    this.calculateBubblesPositions()
    const xAxis = this.createXAxis()
    const yAxis = this.createYAxis()

    return {
      xAxis,
      yAxis,
      visualisationBoundingRect: this.visualisationBoundingRect,
      axesBoundingRect: this.axesBoundingRect,
      bubblesBoundingRect: this.bubblesBoundingRect,
    }
  }

  private reset() {
    this.yAxisZeroPosition = undefined
    this.xAxisTicks = new Map()
    this.yAxisTicks = {}
    this.customLabelsByYValue = {}
    this.axesBottomMargin = 0
    this.axesBoundingRect = new Rectangle({ top: 0, left: 0, bottom: 0, right: 0 })
    this.bubblesBoundingRect = new Rectangle({ top: 0, left: 0, bottom: 0, right: 0 })
  }

  private calculateVisualisationBoundingRect() {
    const { top, left, bottom, right } = this.canvasBoundingRect
    const { isMobile } = typedStore.public.display
    // Allow for the breadcrumbs on mobile
    const topMargin = isMobile ? 100 : 0
    // Allow for the tab overlap on desktop
    const leftMargin = isMobile ? 0 : 30

    this.visualisationBoundingRect = new Rectangle({
      top: top + topMargin,
      left: left + leftMargin,
      bottom,
      right,
    })
  }

  private populateXAxisTicks() {
    this.dimensionsMappings.forEach((mapping) => {
      const { xAxisTickKey, xAxisTickLabel } = mapping

      this.xAxisTicks.set(xAxisTickKey, {
        value: xAxisTickKey,
        label: xAxisTickLabel,
        position: { x: 0, y: 0 },
      })
    })
  }

  private populateYAxisTicks() {
    const { useDistinctValues } = this.config.yAxisTicks

    this.yAxisValues = Array.from(new Set(this.bubbles.map((bubble) => bubble.yAxisValue)))
    const maxValue = Math.max(...this.yAxisValues)
    const minValue = Math.min(...this.yAxisValues)

    let tickSpacing = 1
    if (useDistinctValues) {
      this.yLowerBound = minValue
      this.yUpperBound = maxValue
      this.customLabelsByYValue = this.config.yAxisTicks.customLabels.reduce<
        Record<number, string>
      >((acc, label) => {
        acc[label.value] = label.text
        return acc
      }, {})
    } else {
      const range = Math.max(this.toNiceNumber(maxValue - minValue), 1)
      const maxTicks = Math.min(MAX_Y_TICKS, this.yAxisValues.length)
      tickSpacing = this.toNiceNumber(range / Math.max(maxTicks - 1, 1))
      this.yLowerBound = Math.floor(minValue / tickSpacing) * tickSpacing
      this.yUpperBound = Math.ceil(maxValue / tickSpacing) * tickSpacing
    }

    const ticks: Record<number, AxisTick> = {}
    for (let i = this.yLowerBound; i <= this.yUpperBound; i += tickSpacing) {
      const position: Coordinate2D = {
        x: 0,
        y: this.calculateYPosition(i),
      }

      if (i === 0) {
        this.yAxisZeroPosition = position
      }

      const label = this.getYAxisTickLabel(i)

      ticks[i] = {
        value: i,
        label,
        position,
      }
    }

    this.yAxisTicks = ticks
  }

  private getYAxisTickLabel(yValue: number) {
    let label = ''
    const { showLabels, useDistinctValues } = this.config.yAxisTicks
    if (showLabels) {
      if (useDistinctValues) {
        const customLabel = this.customLabelsByYValue[yValue]
        if (customLabel) {
          label = customLabel
        }
      } else {
        label = this.formatNumericYAxisTickLabel(yValue)
      }
    }

    return label
  }

  private formatNumericYAxisTickLabel(value: number) {
    const label = format('~s')(value).replace(/G/, 'B')

    const { numericUnit } = this.config.yAxisTicks
    if (numericUnit) {
      return `${label} ${numericUnit}`
    }

    return label
  }

  private calculateYAxisLabelVerticalOffset(): number {
    const { show, text } = this.config.axisLabels.y
    if (show && text) {
      const label = new YAxisLabelText()
      label.update({
        label: text,
        startPoint: { x: 0, y: 0 },
        endPoint: { x: 0, y: 0 },
        xOffset: 0,
      })

      return label.height() + YAxisLabelText.bottomMargin
    }

    return 0
  }

  private calculateAxesBottomMargin() {
    this.axesBottomMargin += 100
    if (this.config.axisLabels.x.show) {
      this.axesBottomMargin += XAxisLabelText.margin
    }
  }

  private calculateAxesBoundingBoxHeight() {
    const { top, bottom } = this.visualisationBoundingRect
    const yAxisLabelVerticalOffset = this.calculateYAxisLabelVerticalOffset()
    this.axesBoundingRect = new Rectangle({
      top: top + yAxisLabelVerticalOffset,
      left: 0,
      bottom: bottom - this.axesBottomMargin,
      right: 0,
    })
  }

  private calculateBubblesBoundingBoxHeight() {
    const { top, bottom } = this.axesBoundingRect

    this.bubblesBoundingRect = new Rectangle({
      top: top + BUBBLES_BOX_MARGIN,
      left: 0,
      bottom: bottom - BUBBLES_BOX_MARGIN,
      right: 0,
    })
  }

  private calculateAxesBoundingBoxWidth() {
    const { left, right } = this.visualisationBoundingRect
    const largestBubbleRadius = Math.max(...this.bubbles.map((bubble) => bubble.displacementRadius))
    const noOverlapWidth = largestBubbleRadius * 2 * this.xAxisTicks.size
    const leftMargin = this.calculateYAxisTickLabelsOffset()

    this.axesBoundingRect.left = left + leftMargin
    this.axesBoundingRect.right = Math.max(noOverlapWidth, right)
  }

  private calculateBubblesBoundingBoxWidth() {
    const { left, right } = this.axesBoundingRect
    this.bubblesBoundingRect.left = left + BUBBLES_BOX_MARGIN
    this.bubblesBoundingRect.right = right - BUBBLES_BOX_MARGIN
  }

  private calculateYAxisTickLabelsOffset() {
    const margin = typedStore.public.display.isMobile ? 20 : 75
    const yAxisTickLabelWidths = Object.values(this.yAxisTicks).map(
      (tick) => measureText(tick.label, AxisTickText.fontSize, AxisTickText.fontFamily).width,
    )

    const maxLabelWidth = Math.max(...yAxisTickLabelWidths)
    return Math.min(maxLabelWidth, AxisTickText.maximumWidth) + Y_TICKS_HORIZONTAL_MARGIN + margin
  }

  private calculateBubblesScale() {
    const sumByX: Record<string, number> = {}
    const countByX: Record<string, number> = {}
    this.bubbles.forEach(({ sqrtTotal, xAxisTickKey }) => {
      sumByX[xAxisTickKey] = sumByX[xAxisTickKey] || 0 + sqrtTotal
      countByX[xAxisTickKey] = (countByX[xAxisTickKey] || 0) + 1
    })

    const maxSum = Math.max(...Object.values(sumByX))
    const count = Object.values(countByX)[0] || 1

    const density = _.clamp(1 / count, 0.05, 0.33)
    const availableHeight =
      (this.maximumHeight - this.axesBottomMargin - BUBBLES_BOX_MARGIN * 2) * density
    const baseScale = availableHeight / maxSum / 2

    const largestBubbleRadius = Math.max(
      ...this.bubbles.map((bubble) => bubble.sqrtTotal * baseScale),
    )

    const finalScale =
      largestBubbleRadius > XYBubbleMetadata.maximumRadius
        ? (XYBubbleMetadata.maximumRadius / largestBubbleRadius) * baseScale
        : baseScale

    this.bubbles.forEach((bubble) => {
      bubble.radiusScale = finalScale
    })
  }

  private calculateXAxisTickPositions() {
    const xSpacing = this.bubblesBoundingRect.width / this.xAxisTicks.size
    let xOffset = this.bubblesBoundingRect.left

    let isFirst = true
    this.xAxisTicks.forEach((value) => {
      const spacing = isFirst ? xSpacing / 2 : xSpacing
      value.position = { x: xOffset + spacing, y: this.axesBoundingRect.bottom }
      xOffset = value.position.x

      isFirst = false
    })
  }

  private calculateYAxisTickPositions() {
    Object.values(this.yAxisTicks).forEach((tick) => {
      tick.position.x = this.axesBoundingRect.left - Y_TICKS_HORIZONTAL_MARGIN
    })
  }

  private createXAxis(): XAxisProps {
    const { left, bottom, right } = this.axesBoundingRect
    const y = this.yAxisZeroPosition?.y ?? bottom
    const startPoint = {
      x: left,
      y,
    }
    const endPoint = {
      x: right,
      y,
    }

    const { show, text } = this.config.axisLabels.x

    return {
      label: show ? text : undefined,
      startPoint,
      endPoint,
      ticks: Array.from(this.xAxisTicks.values()),
    }
  }

  private createYAxis(): YAxisProps {
    const { top, left, bottom } = this.axesBoundingRect
    const startPoint = {
      x: left,
      y: top,
    }

    const endPoint = {
      x: left,
      y: bottom,
    }

    const { show, text } = this.config.axisLabels.y

    return {
      label: show ? text : undefined,
      startPoint,
      endPoint,
      xOffset: 0,
      ticks: Object.values(this.yAxisTicks),
    }
  }

  private calculateBubblesPositions() {
    this.bubbles.forEach((bubble) => {
      bubble.destinationPosition = {
        x: this.xAxisTicks.get(bubble.xAxisTickKey)?.position.x ?? 0,
        y: this.calculateYPosition(bubble.yAxisValue),
      }
    })
  }

  private calculateYPosition(value: number) {
    const normalizedValue = (value - this.yLowerBound) / (this.yUpperBound - this.yLowerBound)

    if (isNaN(normalizedValue)) {
      return this.bubblesBoundingRect.bottom / 2
    } else {
      if (this.config.yAxisTicks.useDistinctValues) {
        const margin = this.axesBoundingRect.height / Math.max(this.yAxisValues.length, 1) / 2
        return (
          this.axesBoundingRect.bottom -
          margin -
          normalizedValue * (this.axesBoundingRect.height - margin * 2)
        )
      } else {
        const bottomMargin = this.yLowerBound === 0 ? 0 : BUBBLES_BOX_MARGIN
        return (
          this.bubblesBoundingRect.bottom -
          bottomMargin -
          normalizedValue * this.bubblesBoundingRect.height +
          BUBBLES_BOX_MARGIN
        )
      }
    }
  }

  private toNiceNumber(range: number) {
    const exponent = Math.floor(Math.log10(range))
    const fraction = range / Math.pow(10, exponent)
    let niceFraction

    if (fraction < 1.5) {
      niceFraction = 1
    } else if (fraction < 3) {
      niceFraction = 2
    } else if (fraction < 7) {
      niceFraction = 5
    } else {
      niceFraction = 10
    }

    return niceFraction * Math.pow(10, exponent)
  }
}

export const xyBubblesLayoutCalculator = new XYBubblesLayoutCalculator()
